import React, { useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BarcodeExportConfigurationContext } from '../../../../context/BarcodeExportConfigurationContext';
import { CompanyContext } from '../../../../context/CompanyContext';
import { useToast } from '../../../../providers/ToastProvider';
import {
  BarcodeExportConfigurationInput,
  BarcodeExportConfiguration,
} from '../../../../types/barcode_export_configuration';
import {
  BarcodeExportConfigurationEntityType,
  BarcodeExportConfigurationEntry,
} from '../../../../types/barcode_export_configuration/barcodeExportConfiguration';
import { TranslationKey } from '../../../../types/translation/TranslationKey';
import { translate } from '../../../../types/translation/Translator';
import { Routing } from '../../../../util/routes.util';
import {
  AddButton,
  Button,
  ButtonVariant,
  CancelButton,
  DeleteButton,
  SaveChangesButton,
} from '../../../components/Button';
import { DropdownSelect } from '../../../components/Dropdown';
import Table from '../../../components/Table';
import { Searchbar, TextInput } from '../../../components/TextInput';
import { DeleteIconButton } from '../../../icons';
import BarcodeExportConfigurationEntryModal from './BarcodeExportConfigurationEntryModal';

interface ModalProps {
  entry: BarcodeExportConfigurationEntry | null;
  open: boolean;
}

interface BarcodeExportConfigurationInputPaneProps {
  configuration?: BarcodeExportConfiguration;
  onSave: (configuration: BarcodeExportConfigurationInput) => void;
  onDelete?: () => void;
  loading: boolean;
}

export default function BarcodeExportConfigurationInputPane({
  configuration,
  onSave,
  onDelete,
  loading,
}: BarcodeExportConfigurationInputPaneProps) {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { currentCompany } = useContext(CompanyContext);
  const { barcodeExportConfigurations } = useContext(BarcodeExportConfigurationContext);

  const [input, setInput] = useState<BarcodeExportConfigurationInput>(
    new BarcodeExportConfigurationInput(configuration || { companyId: currentCompany.id }),
  );
  const [modalProps, setModalProps] = useState<ModalProps>({ entry: null, open: false });
  const [query, setQuery] = useState('');

  const resetEntryModal = () => {
    setModalProps({ open: false, entry: null });
  };

  const handleEntityTypeChange = (entityType: BarcodeExportConfigurationEntityType) => {
    if (entityType === input.entityType) return;
    setInput(input.withEntityType(entityType).withEntries([]));
  };

  return (
    <>
      {modalProps.open && (
        <BarcodeExportConfigurationEntryModal
          open={modalProps.open}
          configuration={input}
          entry={modalProps.entry}
          setOpen={open => {
            if (!open) resetEntryModal();
          }}
          onSave={entry => {
            if (modalProps.entry) {
              setInput(input.withEntries(input.entries.map(e => (e === modalProps.entry ? entry : e))));
            } else {
              setInput(input.withEntries([...input.entries, entry]));
            }
            resetEntryModal();
          }}
          onDelete={entryId => {
            setInput(input.withEntries(input.entries.filter(e => e.id !== entryId)));
            resetEntryModal();
          }}
        />
      )}

      <div className='flex h-full flex-col gap-4'>
        <div className='grid gap-2'>
          <div className='r-grid'>
            <TextInput
              value={input.name}
              label={translate({ key: TranslationKey.configurationName })}
              placeholder={translate({ key: TranslationKey.configurationNamePlaceholder })}
              onChange={value => setInput(input.withName(value))}
            />
            <DropdownSelect
              label={translate({ key: TranslationKey.entityType })}
              value={input.entityType}
              items={Object.values(BarcodeExportConfigurationEntityType)}
              onSelect={value => handleEntityTypeChange(value)}
              textBuilder={item => translate({ key: item })}
            />
          </div>
        </div>

        <div className='flex flex-grow flex-col gap-2'>
          <div className='flex justify-between gap-2'>
            <Searchbar value={query} onChange={value => setQuery(value)} />
            <div className='flex gap-2'>
              <Button
                text={translate({ key: TranslationKey.configureLabel })}
                variant={ButtonVariant.secondary}
                onClick={() => navigate(Routing.barcodeLabelEditor.update(input.id))}
              />
              <AddButton onClick={() => setModalProps({ open: true, entry: null })} />
            </div>
          </div>
          <Table<BarcodeExportConfigurationEntry>
            items={input.entries}
            onClick={item => setModalProps({ open: true, entry: item })}
            headers={[
              {
                key: 'field',
                name: translate({ key: TranslationKey.field }),
                text: item => translate({ key: item.field }),
              },
              {
                key: 'format',
                name: translate({ key: TranslationKey.format }),
                text: item => item.format,
              },
              {
                key: 'height',
                name: translate({ key: TranslationKey.height }),
                text: item => item.height.toString(),
              },
              {
                key: 'prefix',
                name: translate({ key: TranslationKey.prefix }),
                text: item => item.prefix || '',
              },
              {
                key: 'suffix',
                name: translate({ key: TranslationKey.suffix }),
                text: item => item.suffix || '',
              },
              {
                key: 'delete',
                name: '',
                text: item => (
                  <DeleteIconButton
                    onClick={() => setInput(input.withEntries(input.entries.filter(e => e !== item)))}
                  />
                ),
              },
            ]}
          />
        </div>
        <div className='flex justify-between'>
          {input.id && onDelete ? <DeleteButton onClick={onDelete} /> : <div> </div>}
          <div className='flex gap-2'>
            <CancelButton onClick={() => navigate(Routing.settings.barcodes.overview())} disabled={loading} />
            <SaveChangesButton
              disabled={!input.validate() || !input.hasChanges(configuration)}
              onClick={() => onSave(input)}
              loading={loading}
            />
          </div>
        </div>
      </div>
    </>
  );
}
